export const ROLES = {
  ADMIN: "admin",
  USER: "user",
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export const ACTIONS = {
  dashboard: {
    read: "dashboard:read",
  },
  products: {
    create: "products:create",
    read: "products:read",
    update: "products:update",
    delete: "products:delete",
    readDetail: "products:readDetail",
  },
  stock: {
    create: "stock:create",
    read: "stock:read",
    update: "stock:update",
    delete: "stock:delete",
  },
  clients: {
    create: "clients:create",
    read: "clients:read",
    update: "clients:update",
    delete: "clients:delete",
  },
  sellers: {
    create: "sellers:create",
    read: "sellers:read",
    update: "sellers:update",
    delete: "sellers:delete",
  },
  contracts: {
    create: "contracts:create",
    read: "contracts:read",
    update: "contracts:update",
    delete: "contracts:delete",
  },
  rentals: {
    create: "rentals:create",
    read: "rentals:read",
    update: "rentals:update",
    delete: "rentals:delete",
    cancel: "rentals:cancel",
    viewUser: "rentals:viewUser",
    createUser: "rentals:createUser",
    createAdmin: "rentals:createAdmin",
    pickup: "rentals:pickup",
  },
  commissions: {
    create: "commissions:create",
    read: "commissions:read",
    update: "commissions:update",
    delete: "commissions:delete",
  },
  commissionRates: {
    create: "commissionRates:create",
    read: "commissionRates:read",
    update: "commissionRates:update",
    delete: "commissionRates:delete",
  },
  devolutions: {
    create: "devolutions:create",
    read: "devolutions:read",
    update: "devolutions:update",
    delete: "devolutions:delete",
  },
  maintenances: {
    create: "maintenances:create",
    read: "maintenances:read",
    update: "maintenances:update",
    delete: "maintenances:delete",
  },
  tags: {
    create: "tags:create",
    read: "tags:read",
    update: "tags:update",
    delete: "tags:delete",
  },
  invoices: {
    create: "invoices:create",
    read: "invoices:read",
    update: "invoices:update",
    delete: "invoices:delete",
    cancel: "invoices:cancel",
  },
  commercialInvoices: {
    create: "commercialInvoices:create",
    read: "commercialInvoices:read",
    cancel: "commercialInvoices:cancel",
  },
  fiscalExport: {
    read: "fiscalExport:read",
  },
  graduations: {
    create: "graduations:create",
    read: "graduations:read",
    update: "graduations:update",
    delete: "graduations:delete",
    cancel: "graduations:cancel",
  },
} as const;

type ExtractPermissions<T> =
  T extends Record<string, infer U>
    ? U extends Record<string, infer V>
      ? V
      : never
    : never;

export type Permission = ExtractPermissions<typeof ACTIONS>;

export const PERMISSIONS: Record<Role, readonly Permission[]> = {
  [ROLES.ADMIN]: [
    ACTIONS.dashboard.read,

    ACTIONS.products.create,
    ACTIONS.products.read,
    ACTIONS.products.update,
    ACTIONS.products.delete,
    ACTIONS.products.readDetail,

    ACTIONS.stock.create,
    ACTIONS.stock.read,
    ACTIONS.stock.update,
    ACTIONS.stock.delete,

    ACTIONS.clients.create,
    ACTIONS.clients.read,
    ACTIONS.clients.update,
    ACTIONS.clients.delete,

    ACTIONS.sellers.create,
    ACTIONS.sellers.read,
    ACTIONS.sellers.update,
    ACTIONS.sellers.delete,

    ACTIONS.contracts.create,
    ACTIONS.contracts.read,
    ACTIONS.contracts.update,
    ACTIONS.contracts.delete,

    ACTIONS.rentals.create,
    ACTIONS.rentals.createAdmin,
    ACTIONS.rentals.read,
    ACTIONS.rentals.update,
    ACTIONS.rentals.delete,
    ACTIONS.rentals.cancel,

    ACTIONS.commissions.create,
    ACTIONS.commissions.read,
    ACTIONS.commissions.update,
    ACTIONS.commissions.delete,

    ACTIONS.commissionRates.create,
    ACTIONS.commissionRates.read,
    ACTIONS.commissionRates.update,
    ACTIONS.commissionRates.delete,

    ACTIONS.tags.create,
    ACTIONS.tags.read,
    ACTIONS.tags.update,
    ACTIONS.tags.delete,

    ACTIONS.invoices.create,
    ACTIONS.invoices.read,
    ACTIONS.invoices.update,
    ACTIONS.invoices.delete,
    ACTIONS.invoices.cancel,

    ACTIONS.commercialInvoices.create,
    ACTIONS.commercialInvoices.read,
    ACTIONS.commercialInvoices.cancel,

    ACTIONS.fiscalExport.read,

    ACTIONS.graduations.create,
    ACTIONS.graduations.read,
    ACTIONS.graduations.update,
    ACTIONS.graduations.delete,
    ACTIONS.graduations.cancel,

    ACTIONS.maintenances.create,
    ACTIONS.maintenances.read,
    ACTIONS.maintenances.update,
    ACTIONS.maintenances.delete,
  ],

  [ROLES.USER]: [
    ACTIONS.dashboard.read,

    ACTIONS.products.readDetail,

    ACTIONS.stock.read,

    ACTIONS.clients.create,
    ACTIONS.clients.read,
    ACTIONS.clients.update,
    ACTIONS.clients.delete,

    ACTIONS.contracts.create,
    ACTIONS.contracts.read,
    ACTIONS.contracts.update,
    ACTIONS.contracts.delete,

    ACTIONS.rentals.create,
    ACTIONS.rentals.read,
    ACTIONS.rentals.update,
    ACTIONS.rentals.cancel,
    ACTIONS.rentals.viewUser,
    ACTIONS.rentals.createUser,
    ACTIONS.rentals.pickup,

    ACTIONS.devolutions.create,
    ACTIONS.devolutions.read,
    ACTIONS.devolutions.update,
    ACTIONS.devolutions.delete,

    ACTIONS.maintenances.create,
    ACTIONS.maintenances.read,
    ACTIONS.maintenances.update,
    ACTIONS.maintenances.delete,

    ACTIONS.tags.create,
    ACTIONS.tags.read,
  ],
} as const;

export function hasPermission(role: Role, permission: Permission): boolean {
  return (PERMISSIONS[role] as readonly Permission[]).includes(permission);
}
